// What a business reads about a QuoteMaker run that stopped before it wrote
// the quote. AI costs are the QuoteMaker operator's only (memory
// feedback_ai_costs_operator_only): a run's spend, its per-job cap, its turn
// count and its model never reach a business. The budget pause's own reason
// names the spend and the cap ("spend $0.80 reached the $0.50 cap"), and a
// failed call's reason carries gateway detail, so neither is shown as it is:
// each stop reads as plain words, and only a reason already written for the
// builder is passed on.
//
// The server strips the run with builderRun() for anyone who is not the
// operator; the QuoteMaker bar and the Assistant word a stop with
// quoteMakerStoppedText(). Pure and shared: the browser imports this file.

// A reason written for the builder: a sentence, with no money, no gateway or
// HTTP detail and no loop internals.
const TECHNICAL_RE = /\$\s?\d|\bUSD\b|\bHTTP\b|\bgateway\b|\btool call|\bfinish reason\b|\bturn\b|\bturns\b|\btoken|\bcap\b|\bbudget\b|\bspend\b|\bmodel\b/i;
const RULE_PREFIX_RE = /^\s*(?:S[1-9]|cap|gate)\s*:\s*/i;

const sentence = (text) => {
    const t = String(text || '').trim().replace(/\s+/g, ' ');
    if (!t) return '';
    const capital = t.charAt(0).toUpperCase() + t.slice(1);
    return /[.!?]$/.test(capital) ? capital : `${capital}.`;
};

/** The run's stop reason without its rule prefix, or '' when it is not one a business may read. */
export function plainStopReason(stopReason) {
    const raw = String(stopReason || '').replace(RULE_PREFIX_RE, '').trim();
    if (!raw || TECHNICAL_RE.test(raw)) return '';
    return sentence(raw);
}

/**
 * What a business reads about why a run stopped, by its stop rule:
 *   S2   the per-job budget pause (its reason names the spend and the cap);
 *   cap  a spend limit at the gateway;
 *   gate the business was switched off;
 *   S5   the builder stopped it;
 *   any other: its own reason when it is plain words, else a general line.
 * Null for a run that has not stopped (or finished normally).
 */
export function builderStopReason({ status, stopRule, stopReason } = {}) {
    const rule = String(stopRule || '').trim();
    if (rule === 'S2') return 'It paused before it finished. Continue carries on from where it stopped.';
    if (rule === 'cap') return 'It paused because QuoteMaker cannot write more for this business today. Continue carries on later.';
    if (rule === 'gate') return plainStopReason(stopReason) || 'It paused because QuoteMaker writing is switched off for this business.';
    if (rule === 'S5' || status === 'cancelled') return 'It was stopped before it finished.';
    if (status === 'failed' || rule === 'S4') return plainStopReason(stopReason) || 'Something went wrong while it was writing.';
    if (status === 'paused') return plainStopReason(stopReason) || 'It paused before it finished.';
    return plainStopReason(stopReason) || null;
}

/**
 * A run as a business may see it: no spend, budget, turn count, model,
 * rulebook or resume count, and its stop reason in plain words. The operator
 * (showCosts) gets the run as it is.
 */
export function builderRun(run, { showCosts = false } = {}) {
    if (!run || showCosts) return run;
    const {
        budgetUsd: _budget, spendUsd: _spend, turnCount: _turns, model: _model, rulebookId: _rulebook, resumeCount: _resumes,
        ...rest
    } = run;
    const stopped = ['paused', 'failed', 'cancelled'].includes(rest.status) || !!rest.stopRule;
    return { ...rest, stopReason: stopped ? builderStopReason(run) : null };
}

/**
 * The bar's and the Assistant's one line for a run that stopped: what happened
 * and what to do. The operator also sees the run's own reason.
 */
export function quoteMakerStoppedText(run, { showCosts = false } = {}) {
    if (!run) return '';
    const status = run.status;
    const why = builderStopReason(run) || '';
    const operatorDetail = showCosts && run.stopReason ? ` (${String(run.stopReason).replace(RULE_PREFIX_RE, '')})` : '';
    if (status === 'paused') return `Paused. ${why}${operatorDetail} The job is as it was.`.replace(/\s+/g, ' ').trim();
    if (status === 'failed') return `Could not finish. ${why}${operatorDetail} The job is as it was.`.replace(/\s+/g, ' ').trim();
    if (status === 'cancelled') return `Stopped. The job is as it was.${operatorDetail}`.trim();
    return '';
}
